// SEO helpers: meta tag prop builder + JSON-LD constructors.

// Canonical category-name -> URL-slug function. SINGLE SOURCE OF TRUTH: imported by the
// product list page (slug -> category resolver), the sitemap generator and the
// prerender script, so /products/<slug> always round-trips to the right category even
// for names with "&", punctuation or spaces, e.g. "Econet USD Data&Bundles" ->
// "econet-usd-data-and-bundles".
export function slugifyCategory(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export function buildMeta({ title, description, canonical, ogImage, ogType = "website" }) {
  const siteName = "Tanaka Life"
  const fullTitle = title ? `${title} · ${siteName}` : siteName
  return {
    title: fullTitle,
    meta: [
      { name: "description", content: description || "" },
      { property: "og:title", content: fullTitle },
      { property: "og:description", content: description || "" },
      { property: "og:type", content: ogType },
      canonical && { property: "og:url", content: canonical },
      ogImage && { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: fullTitle },
      { name: "twitter:description", content: description || "" },
      ogImage && { name: "twitter:image", content: ogImage },
    ].filter(Boolean),
    link: canonical ? [{ rel: "canonical", href: canonical }] : [],
  }
}

export function buildBlogPostJsonLd(article, siteBase) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.seo_title || article.title,
    description: article.seo_description || article.excerpt || "",
    image: article.og_image_url || article.cover_image_url || undefined,
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: { "@type": "Organization", name: "Tanaka Life" },
    publisher: {
      "@type": "Organization",
      name: "Tanaka Life",
      logo: { "@type": "ImageObject", url: `${siteBase}/logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${siteBase}/blog/${article.slug}` },
  }
}

export function buildBlogCollectionJsonLd(siteBase) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Blog · Tanaka Life",
    url: `${siteBase}/blog`,
  }
}

// Product rich-result schema. `offers` lets Google surface price + availability for
// queries like "buy econet airtime". Price may be a single value or a [min, max] range.
export function buildProductJsonLd(product, siteBase) {
  const {
    name,
    description,
    image,
    price,
    currency = "ZAR",
    brand,
    category,
    url,
    availability = "https://schema.org/InStock",
  } = product

  const offers = Array.isArray(price)
    ? {
        "@type": "AggregateOffer",
        priceCurrency: currency,
        lowPrice: price[0],
        highPrice: price[price.length - 1],
        availability,
        url,
      }
    : {
        "@type": "Offer",
        priceCurrency: currency,
        price: price != null ? String(price) : undefined,
        availability,
        url,
      }

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description: description || undefined,
    image: image || `${siteBase}/logo.png`,
    category: category || undefined,
    brand: brand ? { "@type": "Brand", name: brand } : { "@type": "Brand", name: "Tanaka Life" },
    offers,
  }
}

// ItemList for category / product-listing pages — helps Google understand the set of
// products on the page and can drive list-style rich results.
export function buildItemListJsonLd(items, siteBase) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: (items || []).map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: it.url?.startsWith("http") ? it.url : `${siteBase}${it.url || ""}`,
    })),
  }
}

// BreadcrumbList — surfaces the Home › Category › Product trail in search results.
// `crumbs` is an array of { name, url }.
export function buildBreadcrumbJsonLd(crumbs, siteBase) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: (crumbs || []).map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url?.startsWith("http") ? c.url : `${siteBase}${c.url || ""}`,
    })),
  }
}

// FAQPage — eligible for FAQ rich results. `faqs` is an array of { question, answer }.
export function buildFaqJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (faqs || []).map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  }
}
